import { MESSAGES } from '@guuru/chat-common';
import {
  CHAT_FLOW_STEPS,
  ChatAPI,
  appendLocalMessage,
  getCurrentChat,
  setCurrentChatSession,
} from '@guuru/chat-web';
import { client } from '@guuru/graphql-web';
import { delay as sleep } from '@guuru/helpers-common';
import { localize } from '@guuru/translation-common';
import { i18n } from '@guuru/translation-web';
import createChat from '../../commands/createChat';
import CONFIRM_INTENT_SELECTION from './mutations/ConfirmIntentSelection';
import GET_PREDICTION from './queries/IntentSubscription';

const addIntentSelectionMessage = (intents) =>
  sleep(ChatAPI.getBotEllipsisMessageDelay()).then(() => {
    const predictionSelectionSmartForm = 'bot:::predictionSelectionSmartForm';
    const messageStyle = ChatAPI.getMessageStyle();
    const message = ChatAPI.messages.createMessageType(
      null,
      false,
      MESSAGES.intentSelection,
      predictionSelectionSmartForm,
      { messageStyle, intents },
      { hideForExpert: false },
    );
    ChatAPI.pushPendingMessages(message);
    return message;
  });

const intentSelectedMessage = (title) => {
  const locale = ChatAPI.storeRetrieveLocale();
  const messageStyle = ChatAPI.getMessageStyle();
  const intentSelected = ChatAPI.messages.createAnonymousTextMessage(
    localize(title, locale),
    false,
    false,
    { messageStyle },
  );
  ChatAPI.pushPendingMessages(intentSelected);
  return intentSelected;
};

export default {
  // we only need to run this if
  // we have possibleIntents on automationBehavior obj.
  validate(_, { skipAutomation } = {}) {
    const priority = ChatAPI.storeRetrieveChatPriority();
    return !skipAutomation && !priority;
  },

  async render(partnerId) {
    const question = ChatAPI.storeRetrieveQuestion();
    const channel = ChatAPI.storeRetrieveChannel();
    const params = ChatAPI.storeRetrieveParams();
    ChatAPI.storeAutomationBehavior(partnerId, null);
    const {
      data: {
        partner: {
          predict: { intentSubscription, proposedIntent, possibleIntents } = {},
        },
      },
    } = await client.query({
      query: GET_PREDICTION,
      variables: {
        channel,
        partnerId,
        question,
        params,
        languageCode: i18n.language,
      },
    });

    const automationBehavior = {
      ...intentSubscription,
      proposedIntent,
      possibleIntents,
    };
    ChatAPI.storeAutomationBehavior(partnerId, automationBehavior);

    if (!possibleIntents) {
      return {
        goToStep: CHAT_FLOW_STEPS.makePrediction,
        nextStepOptions: {
          automationBehavior,
        },
      };
    }

    await addIntentSelectionMessage(possibleIntents);

    const { chatId, updatedChat } = await createChat(partnerId);

    if (!chatId) return {};

    setCurrentChatSession({
      chatId,
      messages: updatedChat?.messages?.edges,
    });

    return {};
  },
  processResponse(partnerId, { automationBehavior }) {
    const { chatId } = getCurrentChat();
    const { title, label } = automationBehavior;
    const intentMessage = intentSelectedMessage(title);
    appendLocalMessage(intentMessage);

    ChatAPI.storeAutomationBehavior(partnerId, automationBehavior);

    client.mutate({
      mutation: CONFIRM_INTENT_SELECTION,
      variables: {
        input: {
          chatId,
          label,
        },
      },
    });

    return {
      goToStep: CHAT_FLOW_STEPS.makePrediction,
      nextStepOptions: { automationBehavior },
    };
  },
};
